import { CardConfig } from '@affinis/smartus-components/lib/card/card.component.types';
import { Tab } from '@affinis/smartus-components/lib/tabs/tabs.components.types';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AreaReturnViewModel,
  BuildingFaultIncidentViewModel,
  FmDocument,
  FurnitureOrderIncidentViewModel,
  IncidentClient,
  OrderAccessIncidentViewModel,
  RelocationViewModel,
  SendIncidentCommunicationRequest,
  SmartPortalProcessError,
  SpaceRequestViewModel,
  UserAdministrationViewModel,
} from '@core/api';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { FileService } from '@core/services/file-service/file.service';
import { IncidentClass } from '@core/services/ticket-list-desk-service/incidentClass';
import { TicketService } from '@core/services/ticket-list-desk-service/ticket.service';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { getAngularEditorConfig } from '@mandants/telekom/config/angular-editor-config';
import { MtxGridColumn } from '@ng-matero/extensions/grid';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { DocumentVisibility } from '@shared/types/document.types';
import { rxSubscriptionContainerMixin } from '@shared/utils/rx-subscription-container.mixin';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { finalize, from, Observable, shareReplay, switchMap, tap } from 'rxjs';

interface DocumentRow extends FmDocument {
  downloadLoading: boolean;
  archiveToImageMasterLoading: boolean;
  actionsDisabled: boolean;
}

interface IContactForm {
  subject: string;
  content: string;
}

@Component({
  selector: 'app-ticket-summary',
  templateUrl: './ticket-summary.component.html',
  styleUrls: ['./ticket-summary.component.scss'],
  standalone: false,
})
export class TicketSummaryComponent
  extends rxSubscriptionContainerMixin()
  implements OnInit
{
  @Input() incidentId = '';

  @ViewChild('documentActionsTemplate', { static: true })
  documentActionsTemplateRef!: TemplateRef<void>;

  ticketDataBuildingFault$: Observable<BuildingFaultIncidentViewModel>;

  ticketAccessOrderData$: Observable<OrderAccessIncidentViewModel>;

  ticketDataFurnitureOrder$: Observable<FurnitureOrderIncidentViewModel>;

  ticketDataRelocation$: Observable<RelocationViewModel>;

  ticketDataAreaRequest$: Observable<SpaceRequestViewModel>;

  ticketDataAreaReturn$: Observable<AreaReturnViewModel>;

  ticketDataUserAdministration$: Observable<UserAdministrationViewModel>;

  title = this.translateService.instant('ticket.create.helpdesk.summary');

  cardSummary: CardConfig;

  cardDocuments: CardConfig;

  cardContact: CardConfig = {
    footer: {
      button1: {
        label: this.translateService.instant('form.submit'),
        disabled: false,
        onClick: () => {
          this.validateForm();
        },
      },
    },
  };

  tabs: Tab[] = [
    {
      label: this.translateService.instant(
        'ticket.create.label__confirmHeadline'
      ),
      id: 'summary',
      onClick: (id: string) => this.onTabClick(id),
    },
    {
      label: this.translateService.instant('documents.title'),
      id: 'documents',
      onClick: (id: string) => this.onTabClick(id),
    },
  ];

  activeTab = this.tabs[0].id;

  documentVisibilities = [
    {
      value: DocumentVisibility.Intern,
      label: this.translateService.instant('documents.documentVisibilities.0'),
      tooltip: this.translateService.instant(
        'documents.documentVisibilities.tooltip.0'
      ),
    },
    {
      value: DocumentVisibility.InternAndContractors,
      label: this.translateService.instant('documents.documentVisibilities.1'),
      tooltip: this.translateService.instant(
        'documents.documentVisibilities.tooltip.1'
      ),
    },
    {
      value: DocumentVisibility.Public,
      label: this.translateService.instant('documents.documentVisibilities.2'),
      tooltip: this.translateService.instant(
        'documents.documentVisibilities.tooltip.2'
      ),
    },
  ];

  areaRequestColumns: MtxGridColumn[] = [
    {
      header: this.translateService.instant(
        'ticket.create.space_request.requested_area.quantity'
      ),
      field: 'squareMeters',
    },
    {
      header: this.translateService.instant(
        'ticket.create.space_request.requested_area.type'
      ),
      field: 'type',
    },
    {
      header: this.translateService.instant(
        'ticket.create.space_request.requested_area.costcenter'
      ),
      field: 'costCenter.id',
    },
  ];

  roleIncidentColumns: MtxGridColumn[] = [
    {
      header: this.translateService.instant(
        'user_administration.summary.label__ticketType'
      ),
      field: 'incidentClass',
      width: '20%',
    },
    {
      header: this.translateService.instant(
        'user_administration.summary.label__status'
      ),
      field: 'incidentStatus',
    },
    {
      header: this.translateService.instant(
        'user_administration.summary.label__role'
      ),
      field: 'roleName',
    },
    {
      header: this.translateService.instant(
        'user_administration.summary.label__approvalStatus'
      ),
      field: 'approvalStatus',
    },
    {
      header: this.translateService.instant(
        'user_administration.summary.label__reason'
      ),
      field: 'reason',
      width: '20%',
    },
  ];

  columns: MtxGridColumn[] = [];

  incidentClassName: string;

  incidentClassEnum = IncidentClass;

  navigation = this.router.getCurrentNavigation();

  originRoute: string;

  description = '';
  descriptionStyle: string;
  descriptionContainerStyle: string;

  form = new FormGroup({});
  formFields: Array<FormlyFieldConfig> = [];
  formModel = {} as IContactForm;

  isLoading = false;

  angularEditorConfig: AngularEditorConfig = getAngularEditorConfig();

  constructor(
    public incidentClient: IncidentClient,
    private breadcrumbService: BreadcrumbService,
    private translateService: TranslateService,
    private router: Router,
    private toastr: ToastrService,
    private cd: ChangeDetectorRef,
    private fileService: FileService,
    private ticketService: TicketService
  ) {
    super();

    if (this.navigation?.extras?.state) {
      this.incidentClassName = this.navigation.extras.state?.incidentClassName;
      this.originRoute = this.navigation.extras.state?.origin;
    }
  }

  ngOnInit(): void {
    this.initializeContactForm();
    this.description = this.translateService.instant(
      'ticket.summary.contact.hint'
    );
    this.descriptionStyle =
      'background-color: #E20074; color: white; border-radius: 5px; padding: 15px;';
    this.descriptionContainerStyle =
      'width: 100%; max-width: 840px; border-radius: 15px; padding: 0 15px;';

    this.incidentClient.getIncidentAuthenticated(this.incidentId).subscribe({
      next: incident => {
        this.incidentClassName = incident.incidentClass.incidentClass;

        if (IncidentClass.BuildingFault === this.incidentClassName) {
          this.ticketDataBuildingFault$ =
            this.ticketService.getBuildingFaultIncident(this.incidentId);
          this.ticketDataBuildingFault$.subscribe({
            next: data => {
              this.tabs[1].disabled = data.documents.length == 0;
            },
          });
        } else if (IncidentClass.OptionalServices === this.incidentClassName) {
          this.ticketAccessOrderData$ =
            this.ticketService.getOrderAccessIncident(this.incidentId);
          this.ticketAccessOrderData$.subscribe({
            next: data => {
              this.tabs[1].disabled =
                data.documents == null || data.documents.length == 0;
            },
          });
        } else if (IncidentClass.FurnitureOrder === this.incidentClassName) {
          this.tabs.push({
            label: this.translateService.instant(
              'ticket.create.label__summary_contact'
            ),
            id: 'contact',
            onClick: (id: string) => this.onTabClick(id),
          });
          this.ticketDataFurnitureOrder$ =
            this.ticketService.getFurnitureOrderIncident(this.incidentId);
          this.ticketDataFurnitureOrder$?.subscribe({
            next: data => {
              this.tabs[1].disabled =
                data.documents == null || data.documents.length == 0;
            },
          });
        } else if (IncidentClass.Relocation === this.incidentClassName) {
          this.tabs.push({
            label: this.translateService.instant(
              'ticket.create.label__summary_contact'
            ),
            id: 'contact',
            onClick: (id: string) => this.onTabClick(id),
          });
          this.ticketDataRelocation$ = this.ticketService.geRelocationIncident(
            this.incidentId
          );
          this.ticketDataRelocation$?.subscribe({
            next: data => {
              this.tabs[1].disabled =
                data.documents == null || data.documents.length == 0;
            },
          });
        } else if (IncidentClass.AreaRequest === this.incidentClassName) {
          this.ticketDataAreaRequest$ = this.ticketService
            .getAreaRequestIncident(this.incidentId)
            .pipe(
              tap(data => {
                this.tabs[1].disabled =
                  data.documents == null || data.documents.length == 0;
              }),
              shareReplay(1)
            );
        } else if (IncidentClass.AreaReturn === this.incidentClassName) {
          this.ticketDataAreaReturn$ = this.ticketService
            .getAreaReturnIncident(this.incidentId)
            .pipe(
              tap(data => {
                this.tabs[1].disabled =
                  data.documents == null || data.documents.length == 0;
              }),
              shareReplay(1)
            );
        } else if (
          IncidentClass.UserAdministration === this.incidentClassName ||
          IncidentClass.UserCreation === this.incidentClassName ||
          IncidentClass.RoleRequest === this.incidentClassName ||
          IncidentClass.RoleRemoval === this.incidentClassName
        ) {
          this.tabs.splice(1, 1);
          this.ticketDataUserAdministration$ = this.ticketService
            .getUserAdministrationIncident(this.incidentId)
            .pipe(shareReplay(1));
        }
      },
    });

    let label = this.translateService.instant(
      'ticket.create.helpdesk.my_tickets'
    );
    let url = '/helpdesk/my-tickets';

    if (this.originRoute === 'ticketListDtag') {
      label = this.translateService.instant(
        'menu.tickets_tasks.component.tickets'
      );
      url = '/tickets_dtag';
    }

    this.breadcrumbService.setBreadcrumbs([
      {
        label,
        onClick: () => {
          this.router.navigateByUrl(url);
        },
      },
      {
        label: this.title,
      },
    ]);

    this.columns = [
      {
        header: this.translateService.instant('documents.label__title'),
        field: 'filename',
        sortable: true,
      },
      {
        header: this.translateService.instant(
          'documents.label__documentVisibility'
        ),
        field: 'visibilityId',
        width: '15%',
        sortable: true,
        formatter: (data: any) => this.getVisibilityLabel(data.visibilityId),
      },
      {
        header: this.translateService.instant('documents.label__created'),
        field: 'changeDate',
        width: '15%',
        sortable: true,
        formatter: (data: FmDocument) =>
          data.changeDate
            ? moment.utc(data.changeDate).local().format('DD.MM.YYYY, HH:mm:ss')
            : '--',
      },
      {
        header: '',
        width: '120px',
        field: 'action',
        cellTemplate: this.documentActionsTemplateRef,
      },
    ];
  }

  onTabClick(id: string) {
    this.activeTab = id;
  }

  isBuildingFault(): boolean {
    return this.incidentClassName === IncidentClass.BuildingFault;
  }

  isOptionalServices(): boolean {
    return this.incidentClassName === IncidentClass.OptionalServices;
  }

  getVisibilityLabel(visibilityId: string) {
    const found = this.documentVisibilities?.find(
      (option: any) => option.value === visibilityId
    );
    return found?.label ? found.label : '--';
  }

  async downloadDocument(event: Event, row: DocumentRow) {
    if (!row.blobFilename) {
      this.toastr.error(
        this.translateService.instant('detailFields.fileDownload.warn')
      );
      return;
    }

    event.stopPropagation();
    row.actionsDisabled = true;
    row.downloadLoading = true;
    this.cd.detectChanges();

    this.pushSubscription(
      this.fileService
        .downloadFile(row.blobFilename, row.contentType)
        .subscribe({
          next: res => {
            const fileResponse = res;
            row.actionsDisabled = false;
            row.downloadLoading = false;
            this.cd.detectChanges();

            if (fileResponse !== null) {
              this.fileService.saveFile(fileResponse, row.name);
            }
          },
          error: error => {
            if (
              error.errorCode ===
              SmartPortalProcessError.FILESIZE_DOWNLOAD_LIMIT
            ) {
              this.toastr.error(
                this.translateService.instant(
                  'detailFields.fileDownload.filesize_download_limit'
                )
              );
            } else {
              this.toastr.error(
                this.translateService.instant('detailFields.fileDownload.warn')
              );
            }
            row.actionsDisabled = false;
            row.downloadLoading = false;
            this.cd.detectChanges();
          },
        })
    );
  }

  initializeContactForm(): void {
    this.formFields = [
      {
        fieldGroup: [
          {
            key: 'subject',
            type: 'input',
            props: {
              label: `${this.translateService.instant(
                'location.myLocation.location_notification.label__subject'
              )}:`,
              required: true,
            },
            validation: {
              messages: {
                required: this.translateService.instant('formErrors.required', {
                  label: this.translateService.instant(
                    'location.myLocation.location_notification.label__subject'
                  ),
                }),
              },
            },
          },
          {
            key: 'content',
            type: 'textarea',
            props: {
              label: `${this.translateService.instant(
                'ticket.summary.contact.content'
              )}:`,
              required: true,
              rows: 5,
            },
            validation: {
              messages: {
                required: this.translateService.instant('formErrors.required', {
                  label: this.translateService.instant(
                    'ticket.summary.contact.content'
                  ),
                }),
              },
            },
          },
        ],
      },
    ];
  }

  validateForm() {
    if (this.form.valid) {
      this.submitContactForm();
    }
  }

  private resetForm() {
    this.form = new FormGroup({});
    this.formModel = {} as IContactForm;
    this.initializeContactForm();
  }

  submitContactForm() {
    this.isLoading = true;
    this.cd.detectChanges();
    from(this.mapFormToRequest())
      .pipe(
        switchMap(request =>
          this.incidentClient.sendIncidentCommunication(request).pipe(
            finalize(() => {
              this.isLoading = false;
              this.cd.detectChanges();
            })
          )
        )
      )
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translateService.instant(
              'location.myLocation.location_notification.success_message'
            )
          );
          this.resetForm();
        },
        error: () => {
          this.toastr.error(
            this.translateService.instant(
              'location.myLocation.location_notification.error_message'
            )
          );
        },
      });
  }

  private async mapFormToRequest(): Promise<SendIncidentCommunicationRequest> {
    const request: SendIncidentCommunicationRequest = {
      incidentId: this.incidentId,
      incidentClass: this.incidentClassName,
      subject: this.formModel.subject,
      message: this.formModel.content,
    };

    return request;
  }
}
